import React, {  memo, useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router';
import { TFunction, useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import styled from 'styled-components/macro';
import cc, { CurrencyCodeRecord } from 'currency-codes';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';

import { State } from '../reducer';
import Page, { Title } from '../../../components/Page';
import DetailedItems, { defaultItem, itemsAmount } from './DetailedItems';
import PaymentProvidersSelect from './PaymentProvidersSelect';
import { Checkbox, Email, Input, PhoneNumber, Radiobutton, ValidationState } from '../../../components/Forms';
import { Button, ButtonGroup, ButtonLink, IconButton } from '../../../components/Buttons';
import { H3, P } from '../../../components/Typography';
import { LoadingOverlay } from '../../../components/Loading';
import { colors, distances } from '../../../styles/constants';
import { CheckoutConfig, OrderItem, Session, Store } from '../../../types/checkout';
import { toString as composePhoneNumber } from '../../../helpers/phoneNumber';
import { validateValues } from '../validation/validateFormValues';
import { DetailedItemValue } from '../types';
import Icon from '../../../components/Icons';
import { vatAmount } from './vat';
import { fromMonetaryAmountString } from '../../../helpers/formatters';
import LocationsDropdown from '../../../components/Forms/LocationsDropdown';
import { SalesLocation } from '../../../types/customers';
import { getPreviousSettings, setPreviousSettings } from '../localStorage';
import { useIsAllowed } from '../../../auth/accessToken/Allow';
import { Account } from '../../../types/management-auth';
import ExpiryDropdown, { Expiry } from './ExpiryDropdown';
import { Allow } from '../../../auth/accessToken';
import { getPhonePrefixFromCurrency } from '../../../helpers/countryPrefix';
import { useLocation } from 'react-router-dom';
import { useActions } from '../../../Actions';
import { useSelector } from 'react-redux';

interface VirtualTerminalProps {
    virtualTerminalState: State;
    fetchCheckoutConfig: (accountId: string, fetchLocations: boolean) => void;
    createCheckoutSession: (accountId: string, session: Session, email?: boolean) => void;
    clearData: () => void;
}

export interface FormValues {
    payments: string[];
    items: DetailedItemValue[];
    merchantReference: string;
    phoneNumber: string;
    countryPrefix: string;
    email: string;
    sendBy: 'sms' | 'email' | 'link';
    currency: CurrencyCodeRecord;
    autoCapture: boolean;
    location?: SalesLocation;
    defaultVat?: string;
    expiry: Expiry;
    express: boolean;
}

const resolveDefaultVat  = (currency: CurrencyCodeRecord) => {
    switch (currency.code) {
        case 'NOK':
            return '25';
        case 'SEK':
            return '25';
        default:
            return '0';
    }
};

const asCheckoutSessionStore = (salesLocation: SalesLocation): Store => {
    const { location_id: id, name, business_name, address, organization_number, email, phone_number } = salesLocation;
    return {
        id: id || '',
        name,
        business_name,
        address,
        organization_number,
        email,
        phone_number,
    };
};

type Provider =  'instabank' | 'netaxept' | 'payex' | 'collector' | 'santander' | 'swish' | 'bambora';
export type SessionMerchant = { merchant?: {
    name?: string;
    logo_url?: string;
};};

const merchantLogo = (account?: Account, config?: CheckoutConfig) => {
    const fallback = 'https://checkout.test.dintero.com/assets/images/merchants/default.png';
    const configLogo = config?.configuration?.merchant?.logo_url;
    const configLogoIsUndefinedOrFallback = !configLogo || configLogo === fallback;
    const accountLogo = account?.company?.branding?.icon_url || account?.company?.branding?.logo_url;
    if (configLogoIsUndefinedOrFallback && accountLogo) {
        return accountLogo;
    }
    return configLogo || fallback;
};

const expiresAt = (expiry: Expiry) => {
    const now = new Date();
    if (expiry ==='1_month') {
        return addMonths(now, 1).toISOString();
    }
    if (expiry ==='1_week') {
        return addDays(now, 7).toISOString();
    }
    return addDays(now, 1).toISOString();
};

const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

const suggestNewOrderId = (
    hasSession: boolean,
    t: TFunction,
    onClick: () => void,
    hasTouched?: boolean
) => {
    if (hasSession && !hasTouched) {
        return {
            state: ValidationState.Pristine,
            message: <SuggestionLink onClick={onClick}>{t('payments.virtual_terminal.merchant_reference_suggestion')}</SuggestionLink>,
        };
    }
};

const prefillPaymentTypes = (session: Session) => {
    if (!session || !session.configuration) return [];
    const { bambora, instabank, netaxept, payex, vipps, collector, santander, swish, klarna } = session.configuration;
    return [
        ...(!!bambora ? Object.keys(bambora).filter(s => s !== 'type').map(s => 'bambora.'+s) : []),
        ...(!!instabank ? Object.keys(instabank).filter(s => s !== 'type').map(s => 'instabank.'+s): []),
        ...(!!netaxept ? Object.keys(netaxept).filter(s => s !== 'type').map(s => 'netaxept.'+s) : []),
        ...(!!payex ? Object.keys(payex).filter(s => s !== 'type').map(s => 'payex.'+s) : []),
        ...(!!vipps ? ['vipps'] : []),
        ...(!!collector ? Object.keys(collector).filter(s => s !== 'type').map(s => 'collector.'+s): []),
        ...(!!santander ? Object.keys(santander).filter(s => s !== 'type').map(s => 'santander.'+s): []),
        ...(!!swish ? Object.keys(swish).filter(s => s !== 'type').map(s => 'swish.'+s) : []),
        ...(!!klarna ? Object.keys(klarna).filter(s => s !== 'type').map(s => 'klarna.'+s) : []),
    ];
};

const VirtualTerminal = (
    {
        virtualTerminalState,
        fetchCheckoutConfig,
        createCheckoutSession,
        clearData,
    }: VirtualTerminalProps
) => {
    const query = useQuery();
    const hasSessionParam = !!query.get('copySession');
    const { accountId } = useParams();
    const { t, i18n } = useTranslation();
    const canAccessLocations = useIsAllowed([['admin:accounts'], ['admin:locations'], ['read:locations']]);
    const copySession = useSelector(
        (s) => s['sessionDetails']
    );
    const { fetchSession } = useActions('sessionDetails');
    useEffect(() => {
        if (accountId) {
            fetchCheckoutConfig(accountId, canAccessLocations);
            if (hasSessionParam) {
                fetchSession(accountId, query.get('copySession')!);
            }
        }
        return () => clearData();
    }, [accountId, fetchCheckoutConfig, clearData, canAccessLocations, query, fetchSession, hasSessionParam]);

    const paymentOptions = virtualTerminalState.checkoutConfig?.configuration.payment_options ?? [];
    const { locations } = virtualTerminalState;

    const currenciesFromPayments = paymentOptions.reduce(
        (array, option) => [...array, ...option.currencies],
        [] as string[]
    );
    const uniqueCurrencies = [...new Set(currenciesFromPayments)];

    const currencies = uniqueCurrencies
        .map((code) => cc.code(code))
        .filter((code) => code !== undefined) as CurrencyCodeRecord[];


    const currency = currencies.length > 0 ? currencies[0] : (cc.code('NOK') as CurrencyCodeRecord);
    const previousSettings = accountId && getPreviousSettings(accountId);

    const defaultVat = previousSettings?.defaultVat || resolveDefaultVat(currency);

    const initialFormValues: FormValues = {
        payments: [],
        items: [defaultItem(defaultVat)],
        merchantReference: '',
        currency: currency,
        countryPrefix: getPhonePrefixFromCurrency(currency),
        phoneNumber: '',
        email: '',
        sendBy: 'sms',
        autoCapture: false,
        location: undefined,
        defaultVat: defaultVat,
        expiry: '1_day',
        express: false,
    };
    if (previousSettings) {
        initialFormValues.sendBy = previousSettings.sendBy;
        initialFormValues.autoCapture = previousSettings.autoCapture;
        const currency = currencies.find((c) => c.code === previousSettings.currencyCode);
        if (currency) {
            initialFormValues.currency = currency;
        }
        initialFormValues.payments = (previousSettings.payments || []).filter((payment: string) => {
            return paymentOptions.find(
                (x) => x.type === payment && x.currencies.includes(initialFormValues.currency.code)
            );
        });
        if (previousSettings.expiry) {
            initialFormValues.expiry = previousSettings.expiry;
        }
        if (previousSettings.express) {
            initialFormValues.express = previousSettings.express;
        }
        initialFormValues.location = locations.find((x) => x.id === previousSettings.locationId);
    }

    const { checkoutConfig, account, isLoading } = virtualTerminalState;
    if (!checkoutConfig && isLoading && (hasSessionParam && copySession.isLoading)) {
        return (
            <Page title={<Title>{t('payments.virtual_terminal.title')}</Title>} hasEnvFrame={true}>
                <LoadingWrapper>
                    <LoadingOverlay />
                </LoadingWrapper>
            </Page>
        );
    }

    if (hasSessionParam && copySession.session) {
        initialFormValues.items = copySession.session.order?.items?.map(item => ({ id: String(item.id), line_id: String(item.line_id), description: String(item.description), amount: String(((item.amount || 0) / 100) / (item.quantity || 1)), quantity: String(item.quantity), vat: String(item.vat) } as unknown as DetailedItemValue)) as DetailedItemValue[];
        initialFormValues.currency = cc.code(copySession.session.order?.currency)!;
        initialFormValues.countryPrefix = getPhonePrefixFromCurrency(cc.code(copySession.session.order.currency)!);
        initialFormValues.location = copySession.session.order?.store;
        initialFormValues.payments = prefillPaymentTypes(copySession.session);
    }

    const session = virtualTerminalState.createdSession;
    if (session && session.url && session.id) {
        return <Redirect to={`/${accountId}/payments/sessions/${session.id}?from=v2-virtual-terminal`} push={true} />;
    }

    const onSubmit = (values: FormValues) => {
        if (accountId) {
            const items = values.items.map((item, index) => {
                const itemVat = item.vat ? parseFloat(item.vat) : 0;
                const itemAmount = fromMonetaryAmountString(i18n.language, item.amount, values.currency.code);
                const itemQuantity = parseInt(item.quantity);
                const itemVatAmount = vatAmount(itemAmount * itemQuantity, itemVat);
                return {
                    id: item.id,
                    line_id: index.toString(),
                    description: item.description,
                    amount: itemAmount * itemQuantity,
                    quantity: itemQuantity,
                    vat: itemVat,
                    vat_amount: itemVatAmount,
                };
            });
            const vatAmountTotal = (items as OrderItem[]).reduce((acc: number, item: OrderItem) => {
                const itemVatAmount = item.vat_amount || 0;
                return acc + itemVatAmount;
            }, 0);
            const merchantName = account?.company?.display_name ?? account?.company?.business_name ?? checkoutConfig?.configuration.merchant.name ?? '';
            const merchantLogoUrl = merchantLogo(account, checkoutConfig);

            const checkoutSession: Session & SessionMerchant = {
                order: {
                    amount: fromMonetaryAmountString(
                        i18n.language,
                        itemsAmount(values.items, i18n.language).toString(),
                        values.currency.code
                    ),
                    currency: values.currency.code,
                    merchant_reference: values.merchantReference,
                    vat_amount: vatAmountTotal,
                    items,
                },
                merchant: {
                    name: merchantName,
                    logo_url: merchantLogoUrl,
                },
                url: {
                    return_url: 'https://checkout.dintero.com/result/',
                },
                configuration: {
                    auto_capture: values.autoCapture,
                },
                expires_at: expiresAt(values.expiry),
            };
            if (values.sendBy === 'sms') {
                checkoutSession.configuration.publish = [
                    {
                        type: 'checkout-link',
                        channel: 'sms',
                    },
                ];
            }
            if (values.location && values.location.id) {
                checkoutSession.order.store = asCheckoutSessionStore(values.location);
            }
            values.payments.forEach((payment) => {
                const [provider, method] = payment.split('.');

                if (!method) {
                    checkoutSession.configuration[provider as 'vipps'] = {
                        enabled: true,
                    };
                } else {
                    checkoutSession.configuration[provider as Provider] = {
                        ...(checkoutSession.configuration[provider as Provider] || {}),
                        [method]: {
                            enabled: true,
                        },
                    };
                }
            });
            if (values.sendBy === 'sms' && values.phoneNumber) {
                const phoneNumber = composePhoneNumber({
                    countryPrefix: values.countryPrefix,
                    phoneNumber: values.phoneNumber,
                });
                checkoutSession.customer = {
                    phone_number: phoneNumber,
                };
            }
            if (values.sendBy === 'email' && values.email) {
                checkoutSession.customer = {
                    email: values.email,
                };
            }
            if (values.express) {
                checkoutSession.express = {
                    shipping_options: [{ id: '0', line_id: Math.random().toString(36).substring(7), amount: 0, operator: '', title: '', delivery_method: 'none' }],
                };
            }
            if (account?.company.terms_url) {
                checkoutSession.url.merchant_terms_url = account?.company.terms_url;
            }
            createCheckoutSession(accountId, checkoutSession, values.sendBy === 'email');
            // save settings for next payment
            setPreviousSettings(accountId, {
                currencyCode: values.currency.code,
                locationId: values.location?.id,
                payments: values.payments,
                autoCapture: values.autoCapture,
                sendBy: values.sendBy,
                defaultVat: values.defaultVat,
                expiry: values.expiry,
                express: values.express,
            });
        }
    };

    const getSendButtonText = (values: FormValues) => {
        if (
            values.sendBy === 'sms' &&
            values.payments.length === 1 &&
            (values.payments[0].startsWith('swish') || values.payments[0] === 'vipps')
        ) {
            return t('payments.virtual_terminal.buttons.send_by.push_notification');
        } else if (values.sendBy === 'sms') {
            return t('payments.virtual_terminal.buttons.send_by.checkout_sms');
        } else if (values.sendBy === 'email') {
            return t('payments.virtual_terminal.buttons.send_by.link_by_email');
        } else if (values.sendBy === 'link') {
            return t('payments.virtual_terminal.buttons.send_by.payment_link');
        }
    };

    const hasPaymentMethods = (checkoutConfig?.configuration?.payment_options || []).length > 0;

    return (
        <Page title={<Title>{t('payments.virtual_terminal.title')}</Title>} hasEnvFrame={true}>
            <Formik
                enableReinitialize
                initialValues={initialFormValues}
                onSubmit={(values) => onSubmit(values)}
                validate={(values) => validateValues(values, t)}
            >
                {({
                    values,
                    errors,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    setFormikState,
                    touched,
                    resetForm,
                    handleSubmit,
                }) => {
                    return (
                        <Wrapper>
                            {!hasPaymentMethods && (<>
                                <SubCardTitle>
                                    <H3>{t('payments.virtual_terminal.add_payment_connections.title')}</H3>
                                </SubCardTitle>
                                <SubCard>

                                    <P>{t('payments.virtual_terminal.add_payment_connections.body')}</P>
                                    <br />
                                    <Allow anyOfScopes={[['admin'], ['admin:accounts']]}>
                                        <ButtonLink to={`/${accountId}/settings/payment-connections`}>
                                            {t('payments.virtual_terminal.add_payment_connections.call_to_action')}
                                        </ButtonLink>
                                    </Allow>
                                </SubCard>
                            </>)}
                            {account && !account.company.terms_url && <>
                                <SubCardTitle>
                                    <H3>{t('payments.virtual_terminal.no_terms.header')}</H3>
                                </SubCardTitle>
                                <SubCard>
                                    <P>
                                        {t(
                                            'payments.virtual_terminal.no_terms.text'
                                        )}
                                    </P>
                                    <br />
                                    <Allow anyOfScopes={[['admin'], ['admin:accounts']]}>
                                        <ButtonLink to={`/${accountId}/settings`} className="alt">
                                            {t('payments.virtual_terminal.no_terms.call_to_action')}
                                        </ButtonLink>
                                    </Allow>

                                </SubCard></>}
                            <Form
                                className={hasPaymentMethods ? '' : 'disabled'}
                                autoComplete="nope"
                                onSubmit={handleSubmit}
                            >

                                {hasPaymentMethods && (
                                    <>
                                        <SubCardTitle>
                                            <H3>{t('payments.virtual_terminal.subtitles.payment_method')}</H3>
                                        </SubCardTitle>
                                        <SubCard>
                                            <PaymentProvidersSelect
                                                touched={touched}
                                                values={values}
                                                currencies={currencies}
                                                setFieldValue={setFieldValue}
                                                paymentsMethods={paymentOptions}
                                                validation={
                                                    touched.payments && errors.payments
                                                        ? {
                                                            state: ValidationState.Invalid,
                                                            message: errors.payments as string,
                                                        }
                                                        : undefined
                                                }
                                            />
                                        </SubCard>
                                    </>
                                )}

                                {locations.length > 0 && (
                                    <SubCard>
                                        <LocationsDropdown
                                            name="location"
                                            label={t('payments.virtual_terminal.fields.location')}
                                            options={locations}
                                            placeholder={t(
                                                'payments.virtual_terminal.placeholders.location_default'
                                            )}
                                            value={values.location}
                                            onChange={(value) => {
                                                setFieldValue('location', value);
                                                if (value?.currency && value?.currency !== values.currency.code) {
                                                    setFieldValue('currency', cc.code(value.currency));
                                                }
                                            }}
                                        />
                                    </SubCard>
                                )}
                                <SubCardTitle>
                                    <H3>{t('payments.virtual_terminal.subtitles.items')}</H3>
                                    <IconButton
                                        onClick={() => {
                                            const newItems = [...values.items, defaultItem(values.defaultVat)];
                                            setFieldValue('items', newItems, true);
                                        }}
                                        icon={<Icon icon="plus" />}
                                        className="alt"
                                        placement="right"
                                        type="button"
                                    >
                                        <P>{t('payments.virtual_terminal.buttons.add_items')}</P>
                                    </IconButton>
                                </SubCardTitle>
                                <SubCard>
                                    <ItemsWrapper>
                                        <DetailedItems
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            items={values.items}
                                            currency={values.currency}
                                            errors={errors}
                                            shouldAutoFocusOnNewItem={false}
                                            touched={touched}
                                            setFieldTouched={setFieldTouched}
                                            setFormikState={setFormikState}
                                            setFieldValue={setFieldValue}
                                            defaultVat={values.defaultVat}
                                        />
                                    </ItemsWrapper>
                                    <ItemButtonWrapper>
                                        <IconButton
                                            onClick={() => {
                                                const newItems = [...values.items, defaultItem(values.defaultVat)];
                                                setFieldValue('items', newItems, true);
                                            }}
                                            icon={<Icon icon="plus" />}
                                            className="alt"
                                            placement="right"
                                            type="button"
                                        >
                                            <P>{t('payments.virtual_terminal.buttons.add_items')}</P>
                                        </IconButton>
                                    </ItemButtonWrapper>
                                </SubCard>
                                <SubCard>
                                    <ConfirmationWrapper>
                                        <Input
                                            label={t('payments.virtual_terminal.fields.merchant_reference')}
                                            name="merchantReference"
                                            placeholder={t(
                                                'payments.virtual_terminal.placeholders.merchant_reference'
                                            )}
                                            value={values.merchantReference}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            validation={
                                                touched.merchantReference && errors.merchantReference
                                                    ? {
                                                        state: ValidationState.Invalid,
                                                        message: errors.merchantReference,
                                                    }
                                                    : suggestNewOrderId(hasSessionParam, t, () => {
                                                        setFieldValue('merchantReference', copySession.session?.order?.merchant_reference ?? '');
                                                    }, values.merchantReference.length > 0)
                                            }
                                        />
                                        <ExpiryDropdown name="expiry" value={values.expiry} onChange={(value: string) => {setFieldValue('expiry', value);}}/>
                                    </ConfirmationWrapper>
                                    <CheckboxWrapper>
                                        <Checkbox
                                            label={t('payments.virtual_terminal.fields.enable_express_checkout')}
                                            checked={values.express}
                                            name="express"
                                            onChange={handleChange}
                                        />
                                    </CheckboxWrapper>
                                    <CheckboxWrapper>
                                        <Checkbox
                                            label={t('payments.virtual_terminal.fields.auto_capture')}
                                            checked={values.autoCapture}
                                            name="autoCapture"
                                            onChange={handleChange}
                                        />
                                    </CheckboxWrapper>
                                </SubCard>
                                <SubCard>
                                    <div>
                                        <H3>{t('payments.virtual_terminal.subtitles.send_by')}</H3>
                                        <RadioButtonsWrapper>
                                            {['sms', 'email', 'link'].map((option) => (
                                                <Radiobutton
                                                    key={option}
                                                    name={option}
                                                    label={t(
                                                            `payments.virtual_terminal.fields.send_by.${option}` as any
                                                    )}
                                                    value={values.sendBy === option}
                                                    onChange={() => setFieldValue('sendBy', option)}
                                                />
                                            ))}
                                        </RadioButtonsWrapper>
                                    </div>

                                    <ConfirmationWrapper>
                                        <ConfirmationSourceInput>
                                            {values.sendBy === 'sms' && (
                                                <PhoneNumber
                                                    name="phoneNumber"
                                                    label={t('payments.virtual_terminal.fields.phone_number')}
                                                    phoneNumber={values.phoneNumber}
                                                    countryPrefix={values.countryPrefix}
                                                    countryPrefixName="countryPrefix"
                                                    setFieldValue={setFieldValue}
                                                    onBlur={handleBlur}
                                                    validation={
                                                        touched.phoneNumber && errors.phoneNumber
                                                            ? {
                                                                state: ValidationState.Invalid,
                                                                message: errors.phoneNumber,
                                                            }
                                                            : undefined
                                                    }
                                                />
                                            )}
                                            {values.sendBy === 'email' && (
                                                <Email
                                                    name="email"
                                                    label={t('payments.virtual_terminal.fields.email')}
                                                    value={values.email}
                                                    placeholder={t('payments.virtual_terminal.placeholders.email')}
                                                    setFieldValue={setFieldValue}
                                                    onBlur={handleBlur}
                                                    validation={
                                                        touched.email && errors.email
                                                            ? {
                                                                state: ValidationState.Invalid,
                                                                message: errors.email,
                                                            }
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </ConfirmationSourceInput>
                                    </ConfirmationWrapper>

                                    <ButtonGroup>
                                        {/** ensure the form triggers validators when enter is pressed in the form */}
                                        <input type="submit" hidden disabled={isLoading ||
                                                checkoutConfig === undefined} />
                                        <Button
                                            type="submit"
                                            disabled={
                                                Object.values(touched).length === 0 ||
                                                Object.values(errors).length > 0 ||
                                                isLoading ||
                                                checkoutConfig === undefined
                                            }
                                        >
                                            {getSendButtonText(values)}
                                        </Button>
                                        <Button type="button" className="alt" onClick={() => resetForm()}>
                                            {t('payments.virtual_terminal.buttons.clear_form')}
                                        </Button>
                                    </ButtonGroup>
                                </SubCard>
                                {virtualTerminalState.isLoading && <LoadingOverlay />}
                            </Form>
                        </Wrapper>
                    );
                }}
            </Formik>
        </Page>
    );
};

const SuggestionLink = styled.a`
    color: ${colors.primary};
    &:hover {
        text-decoration: underline;
    }
`;


const ItemButtonWrapper = styled.div`
    display:flex;
    justify-content: center;
    align-items: center;
`;

const LoadingWrapper = styled.div`
    position: relative;
`;

const SubCard = styled.div`
    padding: ${distances.small};
    box-shadow: 2px 2px 7px 2px ${colors.borderLight};
    background: ${colors.background};
    margin-bottom: ${distances.small};
`;

const SubCardTitle = styled.div`
    display: flex;
    padding: ${distances.small};
    background: ${colors.background};
    margin-bottom: 3px;
    opacity: 0.65;
    justify-content: space-between;
`;

const Wrapper = styled.div`
    max-width: 830px;
`;

const ItemsWrapper = styled.div`
    margin-bottom: ${distances.small};
`;

const ConfirmationWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;

    > * {
        width: 45%;
    }

    @media (max-width: 1100px) {
        > * {
        width: 100%;
        }
    }
`;

const RadioButtonsWrapper = styled.div`
    & > * {
        margin-right: ${distances.small};
    }
`;

const ConfirmationSourceInput = styled.div`
    margin: ${distances.small} 0;
`;

const CheckboxWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.small};
`;

const Form = styled.form`
    position: relative;
    &.disabled {
        opacity: 0.5;
    }
`;

export default memo(VirtualTerminal);
